import { useCallback, useMemo, useState } from 'react';
import { useCustom, useCustomMutation, useInvalidate } from '@refinedev/core';
import { LivestreamSource, LivestreamStatus } from '@masters-club/types';
import { notify } from '@/lib/notify';
import {
  Eye,
  EyeOff,
  Copy,
  ExternalLink,
  RefreshCw,
  RadioTower,
  ArrowLeftRight,
  CheckCircle2,
  CircleDot,
  Circle,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { resolveApiUrl } from '@/providers/api-url';
import { DEFAULT_RTMP_URL, type Livestream, type LivestreamSourcesStatus } from '../types';

const apiBase = resolveApiUrl().replace(/\/$/, '');

interface Props {
  livestream: Livestream;
  canRegenerateKey: boolean;
  onRegenerate: () => void;
  copyToClipboard: (value: string, label: string) => void;
}

const SOURCE_LABEL: Record<LivestreamSource, string> = {
  [LivestreamSource.OBS]: 'OBS',
  [LivestreamSource.ZOOM]: 'Zoom',
};

/**
 * Панель управления источником входящего потока (OBS / Zoom).
 *
 * Один входящий поток отдаётся зрителям, источников может быть два. Переключатель
 * сверху выбирает, инструкцию какого источника показывать; статус-бейджи отражают,
 * публикует ли каждый источник прямо сейчас и какой отдаётся зрителям. Кнопка
 * hot-swap переключает активный источник в прямом эфире (с защитой и подтверждением).
 */
export function StreamSourcePanel({ livestream, canRegenerateKey, onRegenerate, copyToClipboard }: Props) {
  const { id: livestreamId, streamKey, status, activeSource, watchPageUrl } = livestream;
  const isLive = status === LivestreamStatus.LIVE || status === LivestreamStatus.RECONNECTING;

  // Какую инструкцию показываем (по умолчанию — активный источник).
  const [viewSource, setViewSource] = useState<LivestreamSource>(activeSource);
  const [showKey, setShowKey] = useState(false);
  const [confirmSwap, setConfirmSwap] = useState(false);

  const sourcesUrl = `${apiBase}/admin/livestreams/${livestreamId}/sources`;
  const { result, query } = useCustom<LivestreamSourcesStatus>({
    url: sourcesUrl,
    method: 'get',
    queryOptions: {
      // Реалтайм: бэкенд шлёт livestream.source.changed на publish/unpublish/swap.
      // refetchInterval — safety-net пока идёт эфир.
      refetchInterval: isLive ? 5 * 60_000 : false,
    },
  });
  const sourcesData = result?.data as LivestreamSourcesStatus | undefined;

  const statusBySource = useMemo(() => {
    const map = new Map<LivestreamSource, LivestreamSourcesStatus['sources'][number]>();
    for (const s of sourcesData?.sources ?? []) {
      map.set(s.source, s);
    }
    return map;
  }, [sourcesData]);

  const viewStatus = statusBySource.get(viewSource);
  const targetStatus = statusBySource.get(viewSource);

  const { mutate: switchSource, mutation: switchMutation } = useCustomMutation();
  const invalidate = useInvalidate();
  const isSwitching = switchMutation.isPending;

  // publishKey для выбранного источника: из API (точный) либо вычисляемый fallback.
  const publishKey =
    viewStatus?.publishKey ?? (viewSource === LivestreamSource.ZOOM ? `${streamKey}-zoom` : streamKey);

  const isViewingActive = viewSource === activeSource;
  // Кнопка свапа активна только когда: смотрим НЕ активный источник, эфир идёт,
  // и целевой источник публикует живой поток (иначе чёрный экран у зрителей).
  const canSwap = !isViewingActive && isLive && (targetStatus?.publishing ?? false);

  const swapDisabledReason = useMemo(() => {
    if (isViewingActive) {
      return 'Этот источник уже отдаётся зрителям';
    }
    if (!isLive) {
      return 'Переключить источник можно только во время эфира';
    }
    if (!targetStatus?.publishing) {
      return `${SOURCE_LABEL[viewSource]} сейчас не публикует поток`;
    }
    return null;
  }, [isViewingActive, isLive, targetStatus, viewSource]);

  const handleSwap = useCallback(() => {
    switchSource(
      {
        url: `${apiBase}/admin/livestreams/${livestreamId}/sources/switch`,
        method: 'post',
        values: { target: viewSource },
      },
      {
        onSuccess: () => {
          notify.success(`Зрители переключены на ${SOURCE_LABEL[viewSource]}`);
          setConfirmSwap(false);
          void invalidate({ resource: 'livestreams', invalidates: ['detail'], id: livestreamId });
          void query.refetch();
        },
        onError: (error) => {
          setConfirmSwap(false);
          notify.error(error instanceof Error ? error.message : 'Не удалось переключить источник');
        },
      },
    );
  }, [livestreamId, viewSource, switchSource, invalidate, query]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          <RadioTower className="h-4 w-4" />
          Источник входящего потока
        </CardTitle>
        <CardDescription className="text-xs">
          Зрители видят один поток. Источников может быть два — OBS и Zoom — и переключаться между
          ними можно прямо в эфире. Выберите источник ниже, чтобы увидеть его настройки и статус.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Переключатель источника + статусные бейджи */}
        <ToggleGroup
          type="single"
          variant="outline"
          value={viewSource}
          onValueChange={(v) => {
            if (v === LivestreamSource.OBS || v === LivestreamSource.ZOOM) {
              setViewSource(v);
            }
          }}
          className="w-full grid grid-cols-2 gap-1"
        >
          {[LivestreamSource.OBS, LivestreamSource.ZOOM].map((src) => {
            const st = statusBySource.get(src);
            const isActive = src === activeSource;
            return (
              <ToggleGroupItem key={src} value={src} className="flex flex-col items-start gap-1 h-auto py-2">
                <span className="flex items-center gap-2 font-medium">
                  {SOURCE_LABEL[src]}
                  {isActive && (
                    <Badge variant="default" className="text-[10px] px-1.5 py-0 gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      Зрителям
                    </Badge>
                  )}
                </span>
                <span className="flex items-center gap-1 text-xs">
                  {st?.publishing ? (
                    <>
                      <CircleDot className="h-3 w-3 text-emerald-500" />
                      <span className="text-emerald-600 dark:text-emerald-400">Публикует</span>
                      {st.bitrateMbps !== null && (
                        <span className="text-muted-foreground">· {st.bitrateMbps} Мбит/с</span>
                      )}
                    </>
                  ) : (
                    <>
                      <Circle className="h-3 w-3 text-muted-foreground/50" />
                      <span className="text-muted-foreground">Не публикует</span>
                    </>
                  )}
                </span>
              </ToggleGroupItem>
            );
          })}
        </ToggleGroup>

        {query.isError && (
          <p className="text-xs text-destructive">
            Не удалось получить статус источников из MediaMTX. Поля подключения ниже всё равно валидны.
          </p>
        )}

        {/* Кнопка hot-swap — отдать зрителям выбранный источник */}
        {!isViewingActive && (
          <div className="rounded-md border border-sky-500/40 bg-sky-500/5 p-3 space-y-2">
            <div className="flex items-center justify-between gap-3">
              <div className="text-xs">
                <p className="font-semibold flex items-center gap-1.5">
                  <ArrowLeftRight className="h-3.5 w-3.5" />
                  Отдать зрителям {SOURCE_LABEL[viewSource]}
                </p>
                <p className="text-muted-foreground mt-0.5">
                  Зрители переключатся на этот источник. При переключении возможна пауза ~несколько
                  секунд, пока плеер перезагружает поток.
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                disabled={!canSwap || isSwitching}
                onClick={() => setConfirmSwap(true)}
                title={swapDisabledReason ?? 'Переключить зрителей на этот источник'}
              >
                <ArrowLeftRight className="h-4 w-4 mr-1.5" />
                Переключить
              </Button>
            </div>
            {swapDisabledReason && !canSwap && (
              <p className="text-xs text-muted-foreground">{swapDisabledReason}</p>
            )}
          </div>
        )}

        {/* Поля подключения выбранного источника */}
        <div className="space-y-4 pt-1">
          <div>
            <span className="text-xs text-muted-foreground mb-1 block">
              {viewSource === LivestreamSource.OBS ? 'RTMP Server' : 'URL трансляции'}
            </span>
            <div className="flex items-center gap-2">
              <Input value={DEFAULT_RTMP_URL} readOnly className="font-mono text-sm" />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(DEFAULT_RTMP_URL, 'RTMP URL')}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div>
            <span className="text-xs text-muted-foreground mb-1 block">
              {viewSource === LivestreamSource.OBS ? 'Stream Key' : 'Ключ трансляции'}
            </span>
            <div className="flex items-center gap-2">
              <Input
                value={showKey ? publishKey : '•'.repeat(Math.min(publishKey.length, 32))}
                readOnly
                className="font-mono text-sm"
              />
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => setShowKey((v) => !v)}
                title={showKey ? 'Скрыть' : 'Показать'}
              >
                {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => copyToClipboard(publishKey, 'Stream key')}
              >
                <Copy className="h-4 w-4" />
              </Button>
              {/* Пересоздание ключа доступно только на OBS-форме (это базовый ключ
                  трансляции; Zoom-ключ — производный с суффиксом). */}
              {viewSource === LivestreamSource.OBS && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onRegenerate}
                  disabled={!canRegenerateKey}
                  title={canRegenerateKey ? 'Пересоздать ключ' : 'Запрещено во время LIVE'}
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Пересоздать
                </Button>
              )}
            </div>
            {viewSource === LivestreamSource.OBS && !canRegenerateKey && (
              <p className="text-xs text-muted-foreground mt-1">
                Пересоздание stream key запрещено, пока трансляция в эфире.
              </p>
            )}
            {viewSource === LivestreamSource.ZOOM && (
              <p className="text-xs text-muted-foreground mt-1">
                Ключ Zoom производный от основного (суффикс -zoom). Пересоздайте основной ключ на
                вкладке OBS — Zoom-ключ обновится автоматически.
              </p>
            )}
          </div>

          {viewSource === LivestreamSource.ZOOM && (
            <>
              <div>
                <span className="text-xs text-muted-foreground mb-1 block">
                  URL страницы потокового вещания
                </span>
                <div className="flex items-center gap-2">
                  <Input value={watchPageUrl} readOnly className="font-mono text-sm" />
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => copyToClipboard(watchPageUrl, 'URL страницы')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="icon"
                    onClick={() => window.open(watchPageUrl, '_blank', 'noopener')}
                    title="Открыть страницу"
                  >
                    <ExternalLink className="h-4 w-4" />
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  Каждый, кто использует эту ссылку, может смотреть потоковую трансляцию.
                </p>
              </div>

              <div className="rounded-md border border-sky-500/40 bg-sky-500/5 p-3 text-xs space-y-1.5">
                <p className="font-semibold">Как подключить Zoom Custom Live Stream:</p>
                <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                  <li>В Zoom: More → Live on Custom Live Streaming Service → вставить три поля выше.</li>
                  <li>Требуется Zoom Pro/Business лицензия (Custom Live Stream доступен от Pro).</li>
                  <li>Задержка от Zoom до зрителя — 10–20 секунд (особенность Zoom, не настраивается).</li>
                  <li>Битрейт Zoom фиксирован (~4 Мбит/с), под наш cap 10 Мбит/с подходит.</li>
                  <li>Чат, доступы, запись и анти-шеринг работают так же, как с OBS-источником.</li>
                </ul>
              </div>
            </>
          )}
        </div>
      </CardContent>

      <AlertDialog open={confirmSwap} onOpenChange={setConfirmSwap}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Переключить зрителей на {SOURCE_LABEL[viewSource]}?</AlertDialogTitle>
            <AlertDialogDescription>
              Зрители переключатся на источник {SOURCE_LABEL[viewSource]}. На несколько секунд у них
              может появиться пауза, пока плеер перезагружает поток. Текущий источник
              ({SOURCE_LABEL[activeSource]}) продолжит публиковаться и останется доступен для
              обратного переключения.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isSwitching}>Отмена</AlertDialogCancel>
            <AlertDialogAction onClick={handleSwap} disabled={isSwitching}>
              Переключить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
